import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ChevronDownIcon } from './icons';

interface ListFilterTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Display label — e.g. "Programa", "Status". */
  label: string;
  /** When > 0, shown as a secondary-tinted count badge to the right of the label. */
  count?: number;
}

/**
 * Trigger button for filter popovers/dropdowns. Renders label + optional
 * count badge + chevron, designed to live inside `<PopoverTrigger asChild>`
 * or `<DropdownMenuTrigger asChild>` (forwarded ref + spread props let the
 * Radix Slot inject open/close handlers).
 *
 * The dropdown/popover content stays per-feature since each filter has
 * different content (multi-select with search, checkbox list, etc.).
 */
export const ListFilterTrigger = forwardRef<HTMLButtonElement, ListFilterTriggerProps>(
  ({ label, count, className, ...rest }, ref) => {
    return (
      // `font-normal` overrides Button's default `font-medium` — filter chips
      // should read as quieter UI than primary action buttons; the heavier
      // weight gave them the same visual weight as "Criar vaga", muddling
      // the action hierarchy. `px-3` aligns horizontal padding with the
      // `<Input>` primitive so the filter triggers sit flush next to the
      // search field (Button default is `px-4`).
      <Button
        ref={ref}
        variant="outline"
        className={cn('px-3 font-normal text-foreground', className)}
        {...rest}
      >
        {label}
        {count && count > 0 ? (
          <Badge className="ml-1 bg-secondary px-1.5 text-primary">{count}</Badge>
        ) : null}
        <ChevronDownIcon className="ml-1 size-4 text-muted-foreground" />
      </Button>
    );
  }
);
ListFilterTrigger.displayName = 'ListFilterTrigger';
